import type { Rng } from '../rng';
import type { CasinoGame } from './types';

/**
 * Match-three scratch tickets. A ticket's outcome is decided the moment you
 * buy it, like a real one; the six spots are then laid out to match it.
 * Fixed price and always the cheapest thing on a floor: the last-chance
 * game. Returns ~92.5% (raised 2026-09-23 from ~81%), checked in the tests.
 */
export const PRIZE_TABLE: readonly (readonly [number, number])[] = [
  // [multiple of ticket price, probability]
  [100, 1 / 2000],
  [20, 1 / 200],
  [5, 1 / 40],
  [2, 1 / 5],
  [1, 1 / 4],
];

const SPOT_VALUES = PRIZE_TABLE.map(([m]) => m);

export interface ScratchTicket {
  /** Prize multiple printed under each spot. */
  spots: number[];
  revealed: boolean[];
  /** Winning multiple (0 = losing ticket). */
  prize: number;
  price: number;
}

export interface ScratchersState {
  ticket: ScratchTicket | null;
}

/** House Special: 2x winners come up 1 in 4.5 instead of 1 in 5. Scratchers
 *  is never picked as the Special (see casino.ts), so this only matters if
 *  that ever changes. */
export const SPECIAL_PRIZE_TABLE: readonly (readonly [number, number])[] = PRIZE_TABLE.map(([m, p]) => [m, m === 2 ? 1 / 4.5 : p] as const);

export function drawPrize(rng: Rng, special = false): number {
  let roll = rng.next();
  for (const [multiple, p] of special ? SPECIAL_PRIZE_TABLE : PRIZE_TABLE) {
    if (roll < p) return multiple;
    roll -= p;
  }
  return 0;
}

/** Six spots where the prize (if any) appears exactly three times and no
 *  other value appears more than twice. */
export function layOutSpots(prize: number, rng: Rng): number[] {
  const spots: number[] = [];
  const counts = new Map<number, number>();
  if (prize > 0) {
    spots.push(prize, prize, prize);
    counts.set(prize, 3);
  }
  while (spots.length < 6) {
    const value = SPOT_VALUES[rng.int(SPOT_VALUES.length)]!;
    if (value === prize) continue;
    const count = counts.get(value) ?? 0;
    if (count >= 2) continue;
    counts.set(value, count + 1);
    spots.push(value);
  }
  return rng.shuffle(spots);
}

function spotLabel(ticket: ScratchTicket, i: number): string {
  if (!ticket.revealed[i]) return '  ??';
  return `$${ticket.spots[i]! * ticket.price}`.padStart(4);
}

function unscratched(state: ScratchersState): boolean {
  return !!state.ticket && state.ticket.revealed.some((r) => !r);
}

export const scratchers: CasinoGame<ScratchersState> = {
  id: 'scratchers',
  noDealer: true,
  name: 'Scratchers',
  blurb: 'Match three amounts to win that amount.',
  baseMinBet: 1,
  baseMaxBet: 1,
  fixedBet: true,
  specialPerk: 'More 2x winners (1 in 4.5, not 1 in 5).',
  jackpotRule: 'A top-prize (100x) ticket.',
  create: () => ({ ticket: null }),
  locked: unscratched,
  options(state, env) {
    if (unscratched(state)) {
      return [
        { id: 'scratch', label: 'Scratch a spot' },
        { id: 'scratchAll', label: 'Scratch all' },
      ];
    }
    return [{ id: 'buy', label: `Buy ticket ($${env.minBet})` }];
  },
  describe(state) {
    const t = state.ticket;
    if (!t) return 'Match three amounts to win that amount.\n\nTop prize: 100x the ticket.';
    const grid = `${spotLabel(t, 0)} ${spotLabel(t, 1)} ${spotLabel(t, 2)}\n${spotLabel(t, 3)} ${spotLabel(t, 4)} ${spotLabel(t, 5)}`;
    if (unscratched(state)) return `${grid}\n\nKeep scratching.`;
    return t.prize > 0 ? `${grid}\n\nWinner! +$${t.prize * t.price}` : `${grid}\n\nNo match.`;
  },
  act(state, optionId, env) {
    if (optionId === 'buy') {
      const prize = drawPrize(env.rng, env.special);
      const ticket: ScratchTicket = { spots: layOutSpots(prize, env.rng), revealed: Array(6).fill(false), prize, price: env.minBet };
      return { state: { ticket }, bankroll: env.bankroll - env.minBet };
    }
    const t = state.ticket;
    if (!t) return { state, bankroll: env.bankroll };
    const revealed = optionId === 'scratchAll' ? t.revealed.map(() => true) : [...t.revealed];
    if (optionId === 'scratch') {
      const next = revealed.indexOf(false);
      if (next >= 0) revealed[next] = true;
    }
    const ticket = { ...t, revealed };
    if (revealed.some((r) => !r)) return { state: { ticket }, bankroll: env.bankroll };
    const returned = t.prize * t.price;
    return { state: { ticket }, bankroll: env.bankroll + returned, settled: { wagered: t.price, returned }, jackpot: t.prize === 100 };
  },
};
